import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
    CraftDto,
    CraftService,
    FlyFreelyLoggingService,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    PersonsOrganisationDto,
    ShareResourceCommand,
    UnshareResourceCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Angulartics2 } from 'angulartics2';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { OrganisationRelationshipsUiService } from './organisation-relationships-ui.service';

@Component({
    selector: 'shared-rpa',
    styleUrls: ['./common.scss'],
    template: `<h5 [ngClass]="{ readonly: working }">Shared RPAs</h5>
        <div class="input-group">
            <ng-select
                [items]="
                    organisationRpaList$
                        | async
                        | sortBy: 'nickname'
                        | filterOutById: relationship.sharedRpas
                "
                bindValue="id"
                appendTo="body"
                [formControl]="addRpa"
                placeholder="Select RPA to share with {{
                    relationship.childOrganisation.name
                }}"
                [readonly]="working || isReadOnlyAdmin"
                [clearable]="false"
            >
                <ng-template ng-option-tmp let-item="item" let-index="index">
                    <rpa-list-item
                        [item]="item"
                        [showCustodian]="true"
                    ></rpa-list-item>
                </ng-template>
                <ng-template ng-label-tmp let-item="item" let-index="index">
                    <status-icon [rpa]="item" [clickable]="false"></status-icon>
                </ng-template>
            </ng-select>
            <span class="input-group-btn">
                <button
                    type="button"
                    class="btn btn-primary"
                    [disabled]="working || !addRpa.valid || isReadOnlyAdmin"
                    (click)="shareRpa()"
                >
                    Share
                </button>
            </span>
        </div>
        <div>
            <div
                *ngFor="let rpa of relationship.sharedRpas | sortBy: 'nickname'"
                class="top-buffer horizontal-container center-children"
            >
                <rpa-list-item
                    class="shared-item"
                    [item]="rpa"
                    [showCustodian]="true"
                ></rpa-list-item>

                <button
                    *ngIf="!isReadOnlyAdmin"
                    type="button"
                    class="btn btn-tertiary btn-delete"
                    tooltip="Remove sharing of RPA"
                    placement="top"
                    (click)="unshareRpa(rpa)"
                >
                    <span class="fal fa-trash-alt"></span>
                </button>

                <div>
                    <button
                        *ngIf="rpa.custodian == null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set this organisation as the custodian"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="updateCustodian(rpa.id)"
                    >
                        Set as Custodian
                    </button>
                    <button
                        *ngIf="rpa.custodian != null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set custodian back to the owner"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="clearCustodian(rpa.id)"
                    >
                        Clear Custodian
                    </button>
                </div>
            </div>
        </div>`
})
export class SharedRpa implements OnInit, OnDestroy {
    @Input() organisation: PersonsOrganisationDto;
    @Input() relationship: OrganisationRelationshipDto;
    @Input() isReadOnlyAdmin = false;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    organisationRpaList$: Observable<CraftDto[]>;

    addRpa = new FormControl<number>(undefined, [Validators.required]);

    constructor(
        public workTracker: WorkTracker,
        private logging: FlyFreelyLoggingService,
        private angulartics2: Angulartics2,
        private organisationRelationshipService: OrganisationRelationshipService,
        private organisationRelationshipsUi: OrganisationRelationshipsUiService,
        private rpaService: CraftService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit(): void {
        this.organisationRpaList$ =
            this.organisationRelationshipsUi.organisationRpaList$.pipe(
                map(res =>
                    res.filter(
                        item => item.organisationId === this.organisation.id
                    )
                )
            );
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    shareRpa() {
        const cmd: ShareResourceCommand = {
            resourceId: this.addRpa.value
        };
        this.organisationRelationshipService
            .shareRpa(this.organisation.id, this.relationship.id, cmd)
            .subscribe(results => {
                this.addRpa.setValue(undefined);
                this.angulartics2.eventTrack.next({
                    action: 'share-rpa',
                    properties: {
                        category: 'shared-resources',
                        label: results.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    unshareRpa(rpa: CraftDto) {
        const cmd: UnshareResourceCommand = { resourceId: rpa.id };
        this.organisationRelationshipService
            .unshareRpa(this.organisation.id, this.relationship.id, cmd)
            .subscribe(result => {
                this.angulartics2.eventTrack.next({
                    action: 'unshare-rpa',
                    properties: {
                        category: 'shared-resources',
                        label: result.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    updateCustodian(rpaId: number) {
        this.rpaService
            .updateCustodian(rpaId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: this.relationship.childOrganisation.id
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        `Custodian set to ${this.relationship.childOrganisation.name}`
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    clearCustodian(rpaId: number) {
        this.rpaService
            .updateCustodian(rpaId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: null
            })
            .subscribe({
                next: () => this.logging.success('Custodian set back to owner'),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }
}
